import { useEffect, useState } from 'react';
import {
  AzurePolicy,
  EnvironmentRequest,
} from '@costco-service-catalog/bff-types';
import { ClearRounded } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAzurePolicies } from '../../../hooks';
import { removePolicyMode } from '../OfferingForms/useAzureCloudNativeStarterKit';

interface AzurePolicyListProps {
  prodEnvironments: EnvironmentRequest[];
  nonProdEnvironments: EnvironmentRequest[];
  environmentsLength: number;
  handleAzurePolicyListChange: (
    policy: AzurePolicy,
    selectedEnvironment: EnvironmentRequest
  ) => void;
  handleAzurePolicyListModeChange: (
    policy: AzurePolicy,
    selectedEnvironment: EnvironmentRequest
  ) => void;
}

const AzurePolicyList = (props: AzurePolicyListProps) => {
  const {
    prodEnvironments,
    nonProdEnvironments,
    environmentsLength,
    handleAzurePolicyListChange,
    handleAzurePolicyListModeChange,
  } = props;
  const {
    data: azurePolicyList,
    loading: azurePolicyListLoading,
    error: azurePolicyListError,
  } = useAzurePolicies();

  const combinedEnvironments = nonProdEnvironments.concat(prodEnvironments);

  const [tabSelectorIndex, setTabSelectorIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [azurePolicyDisplayList, setAzurePolicyDisplayList] = useState<
    AzurePolicy[]
  >([]);

  useEffect(() => {
    setAzurePolicyDisplayList(azurePolicyList);
  }, [azurePolicyList, azurePolicyListLoading]);

  useEffect(() => {
    setTabSelectorIndex(0);
    setSearch('');
    setAzurePolicyDisplayList(azurePolicyList);
  }, [environmentsLength]);

  const handleChangeTabSelectorIndex = (newValue: number) => {
    setTabSelectorIndex(newValue);
  };

  const handleSearchBoxChange = (searchText: string) => {
    setSearch(searchText);
    const cloneAzurePolicyList = [...azurePolicyList];
    const filteredAzurePolicyList = cloneAzurePolicyList.filter((policy) => {
      const policyText = policy.name
        ?.concat(policy?.displayName || '')
        ?.concat(policy?.description || '')
        .toLowerCase();
      if (policyText?.includes(searchText.toLowerCase())) {
        return true;
      }
      return false;
    });
    setAzurePolicyDisplayList(filteredAzurePolicyList);
  };

  const selectedEnvironmentCheckboxValue = (
    selectedEnvironment: EnvironmentRequest,
    policy: AzurePolicy
  ) =>
    Boolean(
      selectedEnvironment.enabledPolicies?.find(
        (enabledPolicy) =>
          removePolicyMode(enabledPolicy || '') ===
          removePolicyMode(policy.key || '')
      )
    );

  return (
    <Box>
      {combinedEnvironments.length > 0 &&
      combinedEnvironments.length > tabSelectorIndex ? (
        <Box>
          <Box sx={{ paddingBottom: '0.5rem', padding: '1.1rem' }}>
            <Typography variant="h6">Application Policy Selection</Typography>
            <Typography variant="body2">
              Please make a policy selection for each environment by clicking on
              items in the list below.
            </Typography>
            <Typography variant="body2">
              Switch between environments by using the tabs on the left.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              padding: '1rem',
              maxWidth: '60rem',
              minWidth: '20rem',
            }}
          >
            <Tabs
              orientation="vertical"
              value={tabSelectorIndex}
              onChange={(e, v) => handleChangeTabSelectorIndex(v)}
            >
              {combinedEnvironments.map((selectedEnvironment) => (
                <Tab
                  key={selectedEnvironment.name}
                  label={selectedEnvironment.name}
                  sx={
                    selectedEnvironment.type === 'pd'
                      ? {
                          borderLeft: 5,
                          borderLeftColor: 'rgb(51,153,0)',
                        }
                      : {
                          borderLeft: 5,
                          borderLeftColor: 'rgb(255,204,0)',
                        }
                  }
                />
              ))}
            </Tabs>
            {combinedEnvironments.map((selectedEnvironment, index) => (
              <div
                key={selectedEnvironment.name}
                hidden={tabSelectorIndex !== index}
              >
                {tabSelectorIndex === index && (
                  <Box
                    sx={{
                      paddingBottom: 5,
                      paddingLeft: 5,
                      paddingRight: 10,
                      maxHeight: '450px',
                      overflowY: 'auto',
                    }}
                  >
                    <Typography variant="h6">
                      {selectedEnvironment.name}
                    </Typography>
                    <Box sx={{ display: 'flex' }}>
                      <Box
                        sx={{
                          backgroundColor:
                            selectedEnvironment.type === 'pd'
                              ? 'rgba(51,153,0, 0.3)'
                              : 'rgb(255,204,0, 0.4)',
                          borderRadius: 1,
                          paddingX: 2,
                          display: 'flex',
                        }}
                      >
                        <Typography>
                          {selectedEnvironment.type === 'pd'
                            ? 'Production Environment'
                            : 'Non-production Environment'}
                        </Typography>
                      </Box>
                      <Box />
                    </Box>
                    <br />
                    <Box>
                      {azurePolicyListLoading ? (
                        <Box>
                          <CircularProgress size="1rem" />
                        </Box>
                      ) : (
                        <Box>
                          <Box>
                            <TextField
                              variant="standard"
                              placeholder="Search..."
                              value={search}
                              onChange={(e) =>
                                handleSearchBoxChange(e.target.value)
                              }
                            />
                            <Fade in={search.length > 0}>
                              <IconButton
                                size="small"
                                onClick={() => handleSearchBoxChange('')}
                              >
                                <ClearRounded />
                              </IconButton>
                            </Fade>
                          </Box>
                          <Box>
                            <List>
                              {azurePolicyDisplayList.map((policy) => {
                                const labelId = `checkbox-list-label-${policy.key}`;
                                return (
                                  <ListItem
                                    key={policy.key}
                                    dense
                                    disableGutters
                                    disablePadding
                                  >
                                    <ListItemIcon>
                                      <Checkbox
                                        edge="start"
                                        tabIndex={-1}
                                        disableRipple
                                        checked={selectedEnvironmentCheckboxValue(
                                          selectedEnvironment,
                                          policy
                                        )}
                                        inputProps={{
                                          'aria-labelledby': labelId,
                                        }}
                                        onClick={(e) => {
                                          handleAzurePolicyListChange(
                                            policy,
                                            selectedEnvironment
                                          );
                                        }}
                                      />
                                    </ListItemIcon>
                                    <Tooltip
                                      title={policy.description}
                                      placement="bottom-start"
                                    >
                                      <ListItemText
                                        id={labelId}
                                        primary={policy.displayName}
                                      />
                                    </Tooltip>
                                    <ListItemIcon>
                                      <Fade
                                        in={selectedEnvironmentCheckboxValue(
                                          selectedEnvironment,
                                          policy
                                        )}
                                      >
                                        <Box
                                          sx={{
                                            paddingX: '4rem',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Typography variant="body2">
                                            Audit
                                          </Typography>
                                          <Switch
                                            checked={Boolean(
                                              selectedEnvironment.enabledPolicies
                                                ?.find(
                                                  (enabledPolicy) =>
                                                    removePolicyMode(
                                                      enabledPolicy || ''
                                                    ) ===
                                                    removePolicyMode(
                                                      policy.key || ''
                                                    )
                                                )
                                                ?.includes('Deny')
                                            )}
                                            onClick={() =>
                                              handleAzurePolicyListModeChange(
                                                policy,
                                                selectedEnvironment
                                              )
                                            }
                                          />
                                          <Typography variant="body2">
                                            Deny
                                          </Typography>
                                        </Box>
                                      </Fade>
                                    </ListItemIcon>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </div>
            ))}
          </Box>

          <Box sx={{ paddingX: '1.1rem' }}>
            <TableContainer sx={{ maxWidth: '60rem', minWidth: '20rem' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ display: 'flex' }}>
                    <TableCell sx={{ flex: 1 }}>Environment Name</TableCell>
                    <TableCell sx={{ flex: 3 }}>Policy List</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {combinedEnvironments.map((env) => (
                    <TableRow key={env.name} sx={{ display: 'flex' }}>
                      <TableCell sx={{ flex: 1 }}>
                        <Typography>{env.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ flex: 3 }}>
                        {!env.enabledPolicies ||
                        env.enabledPolicies.length === 0 ? (
                          <Typography variant="body2">
                            None selected.
                          </Typography>
                        ) : undefined}
                        {env.enabledPolicies?.map((ep) => {
                          const policy = azurePolicyList.find(
                            (v) => v.key === removePolicyMode(ep || '')
                          );
                          return (
                            <Chip
                              sx={{ margin: '0.2rem' }}
                              size="small"
                              key={ep}
                              label={policy?.displayName
                                ?.concat(' - ')
                                .concat(
                                  ep && ep.includes('Audit') ? 'Audit' : 'Deny'
                                )}
                            />
                          );
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ) : undefined}
    </Box>
  );
};

export default AzurePolicyList;
